import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Video, AudioLines, Gauge } from "lucide-react";
import { useSection } from "../../lib/content";
import { Reveal, EASE } from "./Reveal";

const TAB_ICONS = [Video, AudioLines, Gauge];

export const SpecTables = () => {
  const c = useSection("specTables");
  const [active, setActive] = useState(0);
  const tab = c.tabs[active];

  return (
    <div data-testid="spec-tables" className="mt-16">
      <Reveal>
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.18em] font-semibold text-[#024AD8]">
              {c.eyebrow}
            </p>
            <h3 className="mt-3 font-display text-xl sm:text-2xl font-bold tracking-tight text-slate-900">
              {c.title}
            </h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {c.tabs.map((t, i) => {
              const Icon = TAB_ICONS[i % TAB_ICONS.length];
              const isOn = i === active;
              return (
                <button
                  key={t.label}
                  data-testid={`spec-tab-${i}`}
                  onClick={() => setActive(i)}
                  className={`inline-flex items-center gap-2 text-sm font-semibold px-5 py-2.5 rounded-full border transition-[background-color,color,border-color] duration-300 ${
                    isOn
                      ? "bg-[#024AD8] text-white border-[#024AD8]"
                      : "bg-[#1a1a1a] text-slate-300 border-[#2a2a2a] hover:border-[#024AD8] hover:text-white"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>
      </Reveal>

      <Reveal delay={0.1}>
        <div className="mt-8 rounded-2xl border border-[#2a2a2a] bg-[#1a1a1a] overflow-hidden shadow-[0_24px_60px_-30px_rgba(15,23,42,0.35)]">
          <AnimatePresence mode="wait">
            <motion.div
              key={active}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.35, ease: EASE }}
            >
              <div className="px-7 py-5 border-b border-[#2a2a2a] flex items-center justify-between gap-4 flex-wrap">
                <h4 className="font-display font-bold text-white text-lg tracking-tight">{tab.label}</h4>
                <span className="font-mono text-[10px] uppercase tracking-[0.16em] font-semibold text-slate-500">
                  Gen 2 · X32 / X52 / X72
                </span>
              </div>
              <div data-testid="spec-table" className="overflow-x-auto">
                <table className="w-full text-left min-w-[720px]">
                  <thead>
                    <tr className="border-b-2 border-[#024AD8]">
                      {tab.headers.map((h) => (
                        <th key={h} className="px-7 py-4 font-mono text-[10px] uppercase tracking-[0.16em] font-semibold text-slate-400">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {tab.rows.map((row, i) => (
                      <tr
                        key={row[0]}
                        className={`border-b border-[#262626] last:border-0 transition-colors duration-200 hover:bg-[#232323] ${
                          i % 2 === 0 ? "bg-[#1a1a1a]" : "bg-[#202020]"
                        }`}
                      >
                        {row.map((cell, j) => (
                          <td
                            key={j}
                            className={
                              j === 0
                                ? "px-7 py-4 font-display font-bold text-white whitespace-nowrap align-top"
                                : "px-7 py-4 text-sm text-slate-300 leading-relaxed align-top"
                            }
                          >
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </Reveal>
    </div>
  );
};
